
import { Check } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICompra } from "shared/interfaces/ICompra";
import { avaliarCompra } from "shared/services/api/compras.api";
import { getStatusStyle, formatarNumeroPedido } from "shared/utils/compraStatus";
import { formatarMoeda, reaisParaCentavos } from "../../utils/money";
import { AvaliacaoEstrelas } from "../avaliacaoestrelas/AvaliacaoEstrelas";
import { Input } from "../inputs/Input";
import styles from "./CompraItem.module.scss";

interface ComprasProps {
  compra: ICompra;
}

export const CompraItem: React.FC<ComprasProps> = ({ compra }) => {
  const navigate = useNavigate();
  const [abrirPopup, setAbrirPopup] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [avaliacao, setAvaliacao] = useState<number>(compra.avaliacao ?? 0);
  const [comentario, setComentario] = useState<string>(compra.comentario ?? "");

  const entregue = compra.status === "entregue";
  const jaAvaliado = !!compra.avaliacao;
  const statusInfo = getStatusStyle(compra.status);
  const itens = compra.itens ?? [];
  const itensExibidos = itens.slice(0, 4);
  const quantidadeImagens = Math.min(itensExibidos.length, 4);

  const quantidadeTotal = itens.reduce((total: number, item: any) => {
    return total + Number(item.quantity ?? item.quantidade ?? 1);
  }, 0);

  async function registrarAvaliacao() {
    if (avaliacao === 0) return;

    try {
      await avaliarCompra(compra.id, avaliacao, comentario);
      compra.avaliacao = avaliacao;
      compra.comentario = comentario;
      setAbrirPopup(false);
      setShowConfirm(true);
    } catch (error) {
      console.error("Erro ao avaliar compra", error);
    }
  }

  const formatarData = (data: string) => {
    if (!data) return "";

    return new Date(data).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const abrirDetalhesCompra = () => {
    navigate(`/minhascompras/${compra.id}/detalhes`);
  };

  const tratarTeclado = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      abrirDetalhesCompra();
    }
  };

  return (
    <section className={styles.page}>
      {abrirPopup && (
        <>
          <div className={styles.overlay} onClick={() => setAbrirPopup(false)} />

          <div className={styles.popup}>
            <h3>Avaliar Compra</h3>

            <div className={styles.estrelasPopup}>
              <AvaliacaoEstrelas rating={avaliacao} onChange={setAvaliacao} size={28} />
            </div>

            <Input
              name="comentario"
              placeholder="Conte-nos sobre sua experiência"
              type="textarea"
              value={comentario}
              customClass={styles.customTextArea}
              onChange={(event) => setComentario(event.target.value)}
            />

            <div className={styles.popupBotoes}>
              <button
                type="button"
                className={styles.btnCancelar}
                onClick={() => setAbrirPopup(false)}
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.btnEnviar}
                onClick={registrarAvaliacao}
                disabled={avaliacao === 0}
              >
                Registrar
              </button>
            </div>
          </div>
        </>
      )}

      {showConfirm && (
        <>
          <div className={styles.overlay} onClick={() => setShowConfirm(false)} />

          <div className={styles.confirmBox}>
            <div className={styles.iconOverlay}>
              <div className={styles.iconDiv}>
                <div className={styles.icon}>
                  <Check size={16} />
                </div>
              </div>
            </div>

            <h3>Avaliação registrada!</h3>
            <p>Obrigado pelo feedback.</p>

            <button
              type="button"
              className={styles.btnOk}
              onClick={() => setShowConfirm(false)}
            >
              OK
            </button>
          </div>
        </>
      )}

      <div className={styles.divCompra}>
        <div className={styles.headerPedido}>
          <div className={styles.headerEsquerda}>
            <div className={styles.pedidoStatus}>
              <div className={styles.info}>
                <strong>Pedido</strong>
                <p className={styles.codigo}>#{formatarNumeroPedido(compra.id)}</p>
              </div>

              <span className={`${styles.btnStatus} ${styles[statusInfo.className]}`}>
                {statusInfo.label}
              </span>
            </div>

            <p className={styles.dataCompra}>{formatarData(compra.dataCompra)}</p>
          </div>

          <div className={styles.totalPedido}>
            <span>Total</span>
            <strong>{formatarMoeda(reaisParaCentavos(compra.total ?? 0))}</strong>
          </div>
        </div>

        <div className={styles.divisor} />

        <div
          className={styles.produtoResumo}
          onClick={abrirDetalhesCompra}
          onKeyDown={tratarTeclado}
          role="button"
          tabIndex={0}
          aria-label="Ver detalhes da compra"
        >
          {quantidadeImagens > 0 ? (
            <>
              <div className={`${styles.mosaico} ${styles[`quantidade${quantidadeImagens}`]}`}>
                {itensExibidos.map((item: any, index) => (
                  <span
                    key={item.productId ?? item.id ?? index}
                    className={styles.fatia}
                  >
                    <img
                      src={
                        item.thumbnailUrl ||
                        item.productImage ||
                        item.imagem ||
                        "/imagens/foto.png"
                      }
                      alt={item.productName || item.nome || "Produto"}
                    />
                  </span>
                ))}
              </div>

              <div className={styles.resumoItens}>
                <strong>
                  {quantidadeTotal} {quantidadeTotal === 1 ? "item" : "itens"}
                </strong>

                {itens.length > 4 && <span>+{itens.length - 4} outros produtos</span>}

                <span className={styles.verDetalhes}>Clique para ver os detalhes</span>
              </div>
            </>
          ) : (
            <div className={styles.semImagem}>
              <img src="/imagens/foto.png" alt="Produto" />

              <div className={styles.resumoItens}>
                <strong>
                  {quantidadeTotal} {quantidadeTotal === 1 ? "item" : "itens"}
                </strong>

                <span className={styles.verDetalhes}>Clique para ver os detalhes</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.containerAvaliar}>
          <div className={styles.avaliacaoAtual}>
            <AvaliacaoEstrelas rating={compra.avaliacao ?? 0} />
          </div>

          <p
            className={`${styles.avaliarTexto} ${
              !entregue ? styles.disabled : jaAvaliado ? styles.done : styles.active
            }`}
            onClick={() => {
              if (!entregue || jaAvaliado) return;

              setAvaliacao(compra.avaliacao ?? 0);
              setComentario(compra.comentario ?? "");
              setAbrirPopup(true);
            }}
          >
            {jaAvaliado ? "Avaliado" : "Avalie sua compra"}
          </p>
        </div>
      </div>
    </section>
  );
};
